import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout, ChevronDown, Check } from 'lucide-react';
import { LAYOUT_PRESETS, resolveLayout } from './registry';

// Dropdown that lets the user switch their dashboard layout to a curated
// preset (Default / Personal / Manager / System Health / Minimal). The
// resolveLayout pass filters out widgets the user doesn't have access to,
// so picking a preset designed for a different role is safe.
export default function PresetsMenu({ onApply, authCtx }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const apply = (preset) => {
    if (!window.confirm(t('dashboard.widgets.confirmPreset', { name: t(preset.nameKey), defaultValue: `Switch dashboard to "${t(preset.nameKey)}" layout? Your current arrangement will be overwritten.` }))) return;
    const resolved = resolveLayout(preset.layout, authCtx);
    onApply(resolved);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => setOpen(v => !v)}
        title={t('dashboard.widgets.presets', 'Layout presets')}
      >
        <Layout size={13} /> {t('dashboard.widgets.presets', 'Presets')} <ChevronDown size={11} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 10, padding: 6,
            boxShadow: 'var(--shadow-lg)',
            zIndex: 100, minWidth: 280,
          }}
        >
          <div style={{
            padding: '6px 10px 8px',
            fontSize: 10, fontWeight: 700,
            color: 'var(--c-text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.6px',
          }}>
            {t('dashboard.widgets.presetsHeader', 'Switch layout to…')}
          </div>
          {LAYOUT_PRESETS.map(preset => (
            <button
              key={preset.id}
              onClick={() => apply(preset)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                width: '100%', padding: '8px 10px',
                border: 'none', background: 'transparent',
                borderRadius: 6, cursor: 'pointer', textAlign: 'left',
                color: 'var(--c-text)',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface-2)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>
                {preset.emoji}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {t(preset.nameKey)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 2, lineHeight: 1.4 }}>
                  {t(preset.descKey)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
